import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowsClockwise,
  DownloadSimple,
  FunnelSimple,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { centsToUsd } from "@/lib/billing";
import {
  AGING_BUCKET_META,
  RECEIPT_KIND_META,
  adjustmentsCsv,
  emptyOperationalFinancialReport,
  financialAttentionRows,
  getOperationalFinancialReport,
  receivablesCsv,
  receiptsCsv,
} from "@/lib/operationalFinancialReporting";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgo() {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
}

function downloadCsv(filename, contents) {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function AdminBillingLedger() {
  const initialAsOf = todayStr();
  const initialFilters = {
    asOf: initialAsOf,
    periodStart: thirtyDaysAgo(),
    periodEnd: initialAsOf,
    query: "",
  };
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [report, setReport] = useState(() => emptyOperationalFinancialReport({
    asOf: initialAsOf,
    periodStart: initialFilters.periodStart,
    periodEnd: initialAsOf,
  }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (nextFilters) => {
    const selected = nextFilters || filters;
    setLoading(true);
    setError(null);
    try {
      const next = await getOperationalFinancialReport({
        asOf: selected.asOf,
        periodStart: selected.periodStart,
        periodEnd: selected.periodEnd,
        query: selected.query,
        limit: 1000,
      });
      setReport(next);
    } catch (err) {
      const message = err.message || "Could not load receivables";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const summary = report.summary || {};
  const aging = report.aging || {};
  const rows = report.rows || [];
  const receipts = report.receipts || [];
  const adjustments = report.adjustments || [];
  const attention = useMemo(() => financialAttentionRows(report), [report]);

  const applyFilters = (event) => {
    event.preventDefault();
    if (draft.periodStart > draft.periodEnd) {
      toast.error("The activity start date must be on or before its end date.");
      return;
    }
    if (draft.periodEnd > draft.asOf) {
      toast.error("The activity period cannot end after the as-of date.");
      return;
    }
    setFilters({ ...draft, query: draft.query.trim() });
  };

  const exportReport = (kind) => {
    const contents = kind === "receipts"
      ? receiptsCsv(report)
      : kind === "adjustments"
        ? adjustmentsCsv(report)
        : receivablesCsv(report);
    downloadCsv(kind + "-" + (report.as_of || filters.asOf) + ".csv", contents);
  };

  return (
    <div data-testid="admin-billing-ledger" className="space-y-6">
      <header>
        <Link
          to="/admin/billing"
          data-testid="ledger-back"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-hover hover:underline"
        >
          <ArrowLeft size={12} weight="bold" /> Back to billing
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="overline text-primary-hover">Billing &amp; finance</p>
            <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Receivables &amp; receipts
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-neutral-600">
              Reconcile issued invoices, recorded family and agency receipts,
              and agency claim adjustments from one canonical invoice view.
              This is operational reporting, not a GAAP financial statement.
            </p>
          </div>
          <button
            type="button"
            data-testid="ledger-refresh"
            onClick={() => load()}
            disabled={loading}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3.5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
          >
            <ArrowsClockwise size={14} weight="bold" />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <form
        data-testid="operational-financial-filters"
        onSubmit={applyFilters}
        className="rounded-2xl border border-neutral-200 bg-white p-4"
      >
        <div className="mb-3 flex items-center gap-2">
          <FunnelSimple size={16} weight="duotone" className="text-primary-hover" />
          <div>
            <p className="text-sm font-semibold text-neutral-900">Report controls</p>
            <p className="text-xs text-neutral-500">As of controls receivables; activity dates control receipts and adjustments.</p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="As of">
            <input type="date" data-testid="ledger-as-of" value={draft.asOf} onChange={(event) => setDraft((value) => ({ ...value, asOf: event.target.value }))} className={INPUT} />
          </Field>
          <Field label="Activity from">
            <input type="date" data-testid="ledger-from" value={draft.periodStart} onChange={(event) => setDraft((value) => ({ ...value, periodStart: event.target.value }))} className={INPUT} />
          </Field>
          <Field label="Activity to">
            <input type="date" data-testid="ledger-to" value={draft.periodEnd} onChange={(event) => setDraft((value) => ({ ...value, periodEnd: event.target.value }))} className={INPUT} />
          </Field>
          <Field label="Family or invoice">
            <input type="search" data-testid="ledger-search" placeholder="Search family or invoice" value={draft.query} onChange={(event) => setDraft((value) => ({ ...value, query: event.target.value }))} className={INPUT} />
          </Field>
          <div className="flex items-end">
            <button type="submit" data-testid="ledger-apply" className="inline-flex min-h-10 w-full items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover">
              Apply report
            </button>
          </div>
        </div>
      </form>

      {error && (
        <section data-testid="operational-financial-error" className="rounded-2xl border border-[#C6851E]/40 bg-[#C6851E]/5 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-neutral-900">Receivables could not load</p>
              <p className="mt-1 text-sm text-neutral-700">{error}</p>
            </div>
            <button type="button" onClick={() => load()} className="rounded-lg border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5">Try again</button>
          </div>
        </section>
      )}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi label="Open receivables" value={centsToUsd(summary.open_balance_cents || 0)} detail={(summary.open_invoice_count || 0) + " issued invoice" + (summary.open_invoice_count === 1 ? "" : "s")} accent />
        <Kpi label="Family balance" value={centsToUsd(summary.family_open_cents || 0)} detail="Family responsibility still open" />
        <Kpi label="Agency balance" value={centsToUsd(summary.agency_open_cents || 0)} detail="Agency responsibility still open" />
        <Kpi label="Cash received" value={centsToUsd(summary.cash_received_cents || 0)} detail="Selected activity period" />
        <Kpi label="Claim variance" value={centsToUsd(summary.agency_claim_variance_cents || 0)} detail="Agency reconciliation to review" warn={Number(summary.agency_claim_variance_cents || 0) > 0} />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <p className="overline text-neutral-500">Aging as of {report.as_of || filters.asOf}</p>
        <p className="mt-1 text-sm text-neutral-600">Balances are grouped from the invoice due date, or issue date when no due date was set.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <AgingCard label="Current" value={aging.current_cents} />
          <AgingCard label="1–30 days" value={aging.age_1_30_cents} />
          <AgingCard label="31–60 days" value={aging.age_31_60_cents} warn />
          <AgingCard label="61–90 days" value={aging.age_61_90_cents} alert />
          <AgingCard label="91+ days" value={aging.age_91_plus_cents} alert />
        </div>
      </section>

      {attention.length > 0 && (
        <section data-testid="operational-financial-attention" className="rounded-2xl border border-amber-300 bg-amber-50/40 p-4">
          <div className="flex items-start gap-2">
            <Warning size={18} weight="duotone" className="mt-0.5 text-amber-800" />
            <div className="min-w-0">
              <p className="font-semibold text-neutral-900">Needs review</p>
              <p className="mt-1 text-sm text-neutral-600">These are signals for an authorized follow-up, never an automatic collection or write-off.</p>
              <ul className="mt-3 grid gap-2 lg:grid-cols-2">
                {attention.map((row) => {
                  const agencyVariance = Number(row.agency_claim_variance_cents || 0);
                  const target = agencyVariance > 0 ? "/admin/billing/subsidies" : "/admin/billing/" + row.family_id;
                  const detail = agencyVariance > 0
                    ? "Agency claim variance " + centsToUsd(agencyVariance, row.currency)
                    : (AGING_BUCKET_META[row.age_bucket] || AGING_BUCKET_META.current).label + " receivable";
                  return (
                    <li key={row.invoice_id}>
                      <Link to={target} data-testid={"operational-financial-attention-" + row.invoice_id} className="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm hover:bg-amber-50">
                        <span className="min-w-0">
                          <span className="block truncate font-semibold text-neutral-900">{row.family_name}</span>
                          <span className="block truncate text-xs text-neutral-600">{detail}</span>
                        </span>
                        <span className="shrink-0 font-mono text-xs font-semibold text-amber-900">{centsToUsd(row.open_cents, row.currency)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 p-4">
          <div>
            <p className="font-semibold text-neutral-900">Receivables</p>
            <p className="mt-1 text-xs text-neutral-500">Canonical invoice total less actual receipts. Agency adjustments remain visible separately and never silently reduce an invoice balance.</p>
          </div>
          <button type="button" data-testid="ledger-export-receivables" disabled={rows.length === 0} onClick={() => exportReport("receivables")} className={SECONDARY_BUTTON}>
            <DownloadSimple size={13} weight="bold" /> Export receivables
          </button>
        </div>
        {report.truncated && <TruncatedNote count={report.row_count} noun="invoices" />}
        {loading && rows.length === 0 ? <LoadingBlock /> : rows.length === 0 ? <EmptyBlock testId="ledger-empty" message="No issued, failed, or paid invoices match this report." /> : (
          <div className="overflow-x-auto">
            <table className="min-w-[880px] w-full text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className="px-4 py-3">Family &amp; invoice</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Responsibility</th>
                  <th className="px-4 py-3 text-right">Received</th>
                  <th className="px-4 py-3 text-right">Open</th>
                  <th className="px-4 py-3">Age</th>
                </tr>
              </thead>
              <tbody>{rows.map((row) => <ReceivableRow key={row.invoice_id} row={row} />)}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <DataCard title="Recorded receipts" subtitle="Cash received in the selected activity period. Payment-provider identifiers are intentionally not included in this export." action={<button type="button" data-testid="ledger-export-receipts" disabled={receipts.length === 0} onClick={() => exportReport("receipts")} className={SECONDARY_BUTTON}><DownloadSimple size={13} weight="bold" /> Export</button>}>
          {report.receipts_truncated && <TruncatedNote count={report.receipt_count} noun="receipts" />}
          {loading && receipts.length === 0 ? <LoadingBlock compact /> : receipts.length === 0 ? <EmptyBlock message="No recorded receipts in this activity period." /> : <ReceiptsTable receipts={receipts} />}
        </DataCard>
        <DataCard title="Agency claim adjustments" subtitle="Claim reconciliation entries only. They are not cash receipts and do not automatically change an invoice receivable." action={<button type="button" data-testid="ledger-export-adjustments" disabled={adjustments.length === 0} onClick={() => exportReport("adjustments")} className={SECONDARY_BUTTON}><DownloadSimple size={13} weight="bold" /> Export</button>}>
          {report.adjustments_truncated && <TruncatedNote count={report.adjustment_count} noun="adjustments" />}
          {loading && adjustments.length === 0 ? <LoadingBlock compact /> : adjustments.length === 0 ? <EmptyBlock message="No agency claim adjustments in this activity period." /> : <AdjustmentsTable adjustments={adjustments} />}
        </DataCard>
      </section>
    </div>
  );
}

const INPUT = "w-full min-h-10 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
const SECONDARY_BUTTON = "inline-flex min-h-9 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60";
const TABLE_HEAD = "bg-neutral-50 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500";

function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>{children}</label>;
}

function Kpi({ label, value, detail, accent, warn }) {
  const border = warn ? "border-amber-300" : accent ? "border-primary/30" : "border-neutral-200";
  const valueTone = warn ? "text-amber-900" : accent ? "text-primary-hover" : "text-neutral-900";
  return <div className={"rounded-2xl border bg-white p-4 " + border}><p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p><p className={"mt-1 font-heading text-2xl font-semibold tracking-tight " + valueTone}>{value}</p><p className="mt-1 text-[11px] text-neutral-500">{detail}</p></div>;
}

function AgingCard({ label, value, warn, alert }) {
  const tone = alert ? "border-[#C6851E]/40 bg-[#C6851E]/5 text-[#8A5A12]" : warn ? "border-amber-300 bg-amber-50/40 text-amber-900" : "border-neutral-200 bg-white text-neutral-900";
  return <div className={"rounded-xl border p-3 " + tone}><p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p><p className="mt-1 font-mono text-lg font-semibold">{centsToUsd(value || 0)}</p></div>;
}

function ReceivableRow({ row }) {
  const age = AGING_BUCKET_META[row.age_bucket] || AGING_BUCKET_META.current;
  const needsAgencyReview = Number(row.agency_claim_variance_cents || 0) > 0;
  const ageDetail = Number(row.open_cents || 0) > 0 ? row.age_days + " day" + (Number(row.age_days) === 1 ? "" : "s") : null;
  return (
    <tr data-testid={"ledger-row-" + row.invoice_id} className="border-t border-neutral-100 align-top hover:bg-neutral-50/50">
      <td className="px-4 py-3"><Link to={"/admin/billing/" + row.family_id} className="font-semibold text-neutral-900 hover:text-primary-hover hover:underline">{row.family_name || "Family"}</Link><p className="mt-0.5 text-xs text-neutral-600">{row.title || "Invoice"}</p><p className="mt-1 text-[11px] uppercase tracking-wide text-neutral-400">{row.invoice_status}</p></td>
      <td className="px-4 py-3 text-neutral-700"><p>{row.due_date || row.issue_date || "—"}</p>{row.due_date && <p className="mt-0.5 text-xs text-neutral-500">Issued {row.issue_date}</p>}</td>
      <td className="px-4 py-3 text-xs text-neutral-700"><p>Family {centsToUsd(row.family_open_cents, row.currency)}</p>{row.has_agency_coverage && <p className={needsAgencyReview ? "mt-1 text-amber-900" : "mt-1"}>Agency {centsToUsd(row.agency_open_cents, row.currency)}</p>}{needsAgencyReview && <p className="mt-1 text-[11px] text-amber-800">Claim variance {centsToUsd(row.agency_claim_variance_cents, row.currency)}</p>}</td>
      <td className="px-4 py-3 text-right font-mono text-neutral-700">{centsToUsd(row.received_cents, row.currency)}</td>
      <td className="px-4 py-3 text-right font-mono font-semibold text-neutral-900">{centsToUsd(row.open_cents, row.currency)}</td>
      <td className="px-4 py-3"><span className={"inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " + ageTone(age.tone)}>{age.label}</span>{ageDetail && <p className="mt-1 text-[11px] text-neutral-500">{ageDetail}</p>}</td>
    </tr>
  );
}

function ReceiptsTable({ receipts }) {
  return <div className="overflow-x-auto"><table className="min-w-[600px] w-full text-sm"><thead><tr className={TABLE_HEAD}><th className="px-4 py-3">Received</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Family</th><th className="px-4 py-3 text-right">Amount</th></tr></thead><tbody>{receipts.map((receipt) => <ReceiptRow key={receipt.receipt_id} receipt={receipt} />)}</tbody></table></div>;
}

function ReceiptRow({ receipt }) {
  const meta = RECEIPT_KIND_META[receipt.receipt_kind] || { label: receipt.receipt_kind, tone: "neutral" };
  return <tr className="border-t border-neutral-100"><td className="px-4 py-3 text-neutral-700">{receipt.received_on}</td><td className="px-4 py-3"><span className={"inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " + receiptTone(meta.tone)}>{meta.label}</span>{receipt.agency_name && <p className="mt-1 text-xs text-neutral-500">{receipt.agency_name}</p>}</td><td className="px-4 py-3"><p className="font-medium text-neutral-900">{receipt.family_name || "—"}</p>{receipt.reference && <p className="mt-1 text-xs text-neutral-500">Ref. {receipt.reference}</p>}</td><td className="px-4 py-3 text-right font-mono font-semibold text-neutral-900">{centsToUsd(receipt.amount_cents, receipt.currency)}</td></tr>;
}

function AdjustmentsTable({ adjustments }) {
  return <div className="overflow-x-auto"><table className="min-w-[560px] w-full text-sm"><thead><tr className={TABLE_HEAD}><th className="px-4 py-3">Recorded</th><th className="px-4 py-3">Family</th><th className="px-4 py-3">Kind</th><th className="px-4 py-3 text-right">Amount</th></tr></thead><tbody>{adjustments.map((adjustment) => <AdjustmentRow key={adjustment.adjustment_id} adjustment={adjustment} />)}</tbody></table></div>;
}

function AdjustmentRow({ adjustment }) {
  const label = String(adjustment.adjustment_kind || "adjustment").replace(/_/g, " ");
  const color = Number(adjustment.amount_cents) < 0 ? "text-[#8A5A12]" : "text-neutral-900";
  return <tr className="border-t border-neutral-100"><td className="px-4 py-3 text-neutral-700">{adjustment.recorded_on}</td><td className="px-4 py-3 font-medium text-neutral-900">{adjustment.family_name || "—"}</td><td className="px-4 py-3 capitalize text-neutral-700">{label}</td><td className={"px-4 py-3 text-right font-mono font-semibold " + color}>{centsToUsd(adjustment.amount_cents, adjustment.currency)}</td></tr>;
}

function DataCard({ title, subtitle, action, children }) {
  return <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"><div className="flex items-start justify-between gap-3 border-b border-neutral-100 p-4"><div><p className="font-semibold text-neutral-900">{title}</p><p className="mt-1 text-xs text-neutral-500">{subtitle}</p></div>{action}</div>{children}</section>;
}

function LoadingBlock({ compact = false }) {
  return <div className={"flex items-center justify-center " + (compact ? "py-8" : "py-16")}><div className="h-7 w-7 animate-spin rounded-full border-2 border-primary/30 border-t-primary" aria-label="Loading report" /></div>;
}

function EmptyBlock({ message, testId }) {
  return <div data-testid={testId} className="px-6 py-10 text-center text-sm text-neutral-500">{message}</div>;
}

function TruncatedNote({ count, noun }) {
  return <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">Showing the first 1,000 of {count} {noun}. Refine the report before exporting.</p>;
}

function ageTone(tone) {
  if (tone === "ok") return "border-primary/30 bg-primary/10 text-primary-hover";
  if (tone === "warn") return "border-amber-300 bg-amber-50 text-amber-900";
  if (tone === "alert") return "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]";
  return "border-neutral-200 bg-neutral-50 text-neutral-600";
}

function receiptTone(tone) {
  if (tone === "primary") return "border-primary/30 bg-primary/10 text-primary-hover";
  if (tone === "secondary") return "border-secondary/50 bg-secondary/30 text-neutral-900";
  return "border-neutral-200 bg-neutral-50 text-neutral-600";
}
